import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

export default function HeroSlider({ slides = [], stats }) {
  const [idx, setIdx] = useState(0);
  const valid = useMemo(() => (slides || []).filter((s) => s?.image), [slides]);

  useEffect(() => {
    if (valid.length < 2) return;
    const t = setInterval(() => setIdx((v) => (v + 1) % valid.length), 7000);
    return () => clearInterval(t);
  }, [valid.length]);

  useEffect(() => {
    if (idx >= valid.length) setIdx(0);
  }, [idx, valid.length]);

  useEffect(() => {
    valid.forEach((slide) => {
      const img = new Image();
      img.src = slide.image;
    });
  }, [valid]);

  if (valid.length === 0) return null;

  return (
    <section className="relative min-h-[88vh] overflow-hidden bg-[#1C1C1C]" data-testid="hero-section">
      {valid.map((s, i) => (
        <div
          key={i}
          className={`hero-slide absolute inset-0 ${i === idx ? "active" : "inactive"}`}
          aria-hidden={i !== idx}
        >
          <img src={s.image} alt={s.title || "Industrial fasteners"} className="absolute inset-0 w-full h-full object-cover" loading={i === 0 ? "eager" : "lazy"} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/95 via-[#1C1C1C]/80 to-[#1C1C1C]/40" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(28,28,28,0.5),rgba(28,28,28,0.5))]" />
        </div>
      ))}

      {/* Vertical accent line */}
      <div className="absolute left-8 top-24 bottom-24 w-px bg-[#C1121F]/40 hidden lg:block" />

      <div className="relative container-x min-h-[88vh] flex items-center pt-24 pb-20">
        <div className="max-w-3xl">
          <div className="overline mb-6 flex items-center gap-3">
            <span className="w-10 h-px bg-[#C1121F]" />
            ISO 9001 · IATF 16949 Certified
          </div>
          <h1 className="h-display text-white" data-testid="hero-title">
            {valid[idx].title || "Industrial Fasteners. Engineered to Last."}
          </h1>
          <p className="mt-6 body-lg text-white/75 max-w-2xl font-body">
            {valid[idx].subtitle}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to={valid[idx].cta_link || "/contact"} className="btn-primary group" data-testid="hero-cta-quote">
              {valid[idx].cta_text || "Request a Quote"}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/products" className="btn-outline-light" data-testid="hero-cta-products">
              Browse Products
            </Link>
          </div>

          {stats && (
            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-3xl">
              {[
                ["years", "Years Experience"],
                ["clients", "Industrial Clients"],
                ["countries", "Export Countries"],
                ["certifications", "Certifications"],
              ].map(([k, label]) => (
                <div key={k} className="border-t border-[#C1121F]/40 pt-3">
                  <div className="font-heading font-bold text-3xl md:text-4xl text-white">{stats[k]}</div>
                  <div className="font-body text-xs uppercase tracking-wider text-white/50 mt-1">{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Slider controls */}
      {valid.length > 1 && (
        <div className="absolute bottom-8 right-8 flex items-center gap-3 z-10">
          <button
            onClick={() => setIdx((v) => (v - 1 + valid.length) % valid.length)}
            aria-label="Previous slide"
            data-testid="hero-prev"
            className="w-11 h-11 border border-white/30 text-white hover:border-[#C1121F] hover:text-[#C1121F] flex items-center justify-center transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="font-heading font-bold text-white text-sm tabular-nums">
            {String(idx + 1).padStart(2, "0")}
            <span className="text-white/40"> / {String(valid.length).padStart(2, "0")}</span>
          </div>
          <button
            onClick={() => setIdx((v) => (v + 1) % valid.length)}
            aria-label="Next slide"
            data-testid="hero-next"
            className="w-11 h-11 border border-white/30 text-white hover:border-[#C1121F] hover:text-[#C1121F] flex items-center justify-center transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}
    </section>
  );
}
